import React from "react";
import classNames from "classnames";

export interface DividerProps {
    orientation?: "horizontal" | "vertical";
    size?: "sm" | "md" | "lg";
    color?: "light" | "medium" | "dark";
    className?: string;
    label?: string;
    labelPosition?: "left" | "center" | "right";
}

const sizeClasses = {
    horizontal: {
        sm: "h-px",
        md: "h-0.5",
        lg: "h-1"
    },
    vertical: {
        sm: "w-px",
        md: "w-0.5", 
        lg: "w-1"
    }
};

const colorClasses = {
    light: "bg-gray-200",
    medium: "bg-gray-300", 
    dark: "bg-gray-400"
};

export const Divider: React.FC<DividerProps> = ({
    orientation = "horizontal",
    size = "sm",
    color = "light",
    className,
    label,
    labelPosition = "center"
}) => {
    const isHorizontal = orientation === "horizontal";
    const sizeClass = sizeClasses[orientation][size];
    const colorClass = colorClasses[color];

    if (label && isHorizontal) {
        return (
            <div className={classNames("relative flex items-center", className)}>
                <div 
                    className={classNames(
                        "flex-grow",
                        sizeClass,
                        colorClass,
                        {
                            "mr-3": labelPosition === "left",
                            "mx-3": labelPosition === "center",
                            "ml-3": labelPosition === "right"
                        }
                    )}
                />
                {labelPosition === "left" && (
                    <span className="text-sm text-gray-500 whitespace-nowrap">{label}</span>
                )}
                {labelPosition === "center" && (
                    <>
                        <span className="px-3 text-sm text-gray-500 whitespace-nowrap bg-white">{label}</span>
                        <div className={classNames("flex-grow", sizeClass, colorClass)} />
                    </>
                )}
                {labelPosition === "right" && (
                    <span className="text-sm text-gray-500 whitespace-nowrap">{label}</span>
                )}
            </div>
        );
    }

    if (isHorizontal) {
        return (
            <div 
                className={classNames(
                    "w-full",
                    sizeClass,
                    colorClass,
                    className
                )}
            />
        );
    }

    // Vertical divider
    return (
        <div 
            className={classNames(
                "h-full",
                sizeClass,
                colorClass,
                className
            )}
        />
    );
};
